'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Zap, Trophy, Calendar, TrendingUp, ChevronRight, Bell, ArrowRight, Star, Activity, Share2, Play, Camera, Menu, X, CheckCircle } from
'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLiveWorldCupFixtures, getStatusLabel, isInPlay, getFlag, type LiveFixture } from '@/lib/api-football';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TickerItem {
  id: string;
  home: string;
  away: string;
  homeScore: number;
  awayScore: number;
  minute: string;
  live: boolean;
}

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  time: string;
  image: string;
  alt: string;
  href: string;
}

interface StatCard {
  id: string;
  icon: React.ElementType;
  label: string;
  value: string;
  sub: string;
  accent: string;
  href: string;
}

interface LiveNewsArticle {
  id: string;
  slug?: string;
  title: string;
  excerpt?: string;
  description?: string;
  content?: string;
  category: string;
  time: string;
  image: string;
  alt: string;
  href: string;
  url?: string;
  url_to_image?: string;
  source_name?: string;
  published_at?: string | null;
}

// ─── Static data ─────────────────────────────────────────────────────────────
const NEWS_ARTICLES_KEYS: {
  id: string;
  titleKey: keyof typeof import('@/locales/en.json')['landing'];
  excerptKey: keyof typeof import('@/locales/en.json')['landing'];
  categoryKey: keyof typeof import('@/locales/en.json')['landing'];
  timeKey: keyof typeof import('@/locales/en.json')['landing'];
  image: string;
  alt: string;
  href: string;
}[] = [];


// ─── No Live Games Ticker ─────────────────────────────────────────────────────
const NO_LIVE_MESSAGES = [
  '⚽ No live games at this time — check back on match days',
  '📅 Next matches coming soon — stay tuned!',
  '🏆 FIFA World Cup 2026 — the biggest tournament on Earth',
  '🔔 Enable notifications to get live score alerts',
  '📊 Check the schedule for upcoming fixtures',
  '🌍 48 teams · 104 matches · 16 host cities',
];

function NoLiveGamesTicker({ label }: { label: string }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % NO_LIVE_MESSAGES.length);
        setVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(cycle);
  }, []);

  return (
    <div
      className="relative overflow-hidden rounded-xl py-2.5 px-4 flex items-center gap-2 sm:gap-3 min-w-0"
      style={{ background: 'hsl(222 40% 9%)', border: '1px solid hsl(222 25% 15%)' }}>
      {/* Label */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="w-2 h-2 rounded-full bg-slate-500 flex-shrink-0" />
        <span className="text-xs font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap hidden sm:inline">
          {label}
        </span>
        <span className="text-xs font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap sm:hidden">
          SCORES
        </span>
      </div>
      <span className="text-slate-700 flex-shrink-0 hidden sm:inline">·</span>
      {/* Rotating message */}
      <span
        className="text-xs text-slate-400 truncate flex-1 transition-all duration-400"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(-6px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}>
        {NO_LIVE_MESSAGES[msgIndex]}
      </span>
      {/* CTA */}
      <Link
        href="/live-games-dashboard"
        className="ml-auto text-xs font-semibold text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1 flex-shrink-0">
        <span className="hidden sm:inline">View Schedule</span>
        <span className="sm:hidden">Schedule</span>
        <ChevronRight size={12} />
      </Link>
    </div>
  );
}

// ─── Live Ticker ──────────────────────────────────────────────────────────────
function LiveTicker() {
  const { t } = useLanguage();

  // June 11, 2026 at 12:00 PM Pacific Time = 19:00 UTC
  const LIVE_DATA_START = new Date('2026-06-11T19:00:00Z');

  const SAMPLE_ITEMS: TickerItem[] = [
  { id: 'sample-1', home: '🇲🇽 Mexico', away: '🇵🇱 Poland', homeScore: 2, awayScore: 1, minute: "67'", live: true },
  { id: 'sample-2', home: '🇺🇸 USA', away: '🏴󠁧󠁢󠁥󠁮󠁧󠁿 England', homeScore: 1, awayScore: 1, minute: "34'", live: true },
  { id: 'sample-3', home: '🇧🇷 Brazil', away: '🇦🇷 Argentina', homeScore: 0, awayScore: 1, minute: 'HT', live: true },
  { id: 'sample-4', home: '🇫🇷 France', away: '🇩🇪 Germany', homeScore: 0, awayScore: 0, minute: 'Jun 11', live: false }];


  const [items, setItems] = useState<TickerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const shouldUseLiveData = () => new Date() >= LIVE_DATA_START;

  const fetchLive = async () => {
    if (!shouldUseLiveData()) {
      setItems(SAMPLE_ITEMS);
      setLoading(false);
      return;
    }
    try {
      const fixtures = await getLiveWorldCupFixtures();
      if (fixtures && fixtures.length > 0) {
        const mapped: TickerItem[] = fixtures.map((f: LiveFixture) => {
          const inPlay = isInPlay(f.fixture.status);
          const isHT = f.fixture.status.short === 'HT';
          const homeName = f.teams.home.logo ?
          f.teams.home.name :
          `${getFlag(f.teams.home.name)} ${f.teams.home.name}`;
          const awayName = f.teams.away.logo ?
          f.teams.away.name :
          `${getFlag(f.teams.away.name)} ${f.teams.away.name}`;
          const minute = isHT ?
          'HT' :
          inPlay && f.fixture.status.elapsed !== null ?
          `${f.fixture.status.elapsed}'` :
          getStatusLabel(f.fixture.status);
          return {
            id: String(f.fixture.id),
            home: homeName,
            away: awayName,
            homeScore: f.goals.home ?? 0,
            awayScore: f.goals.away ?? 0,
            minute,
            live: inPlay || isHT
          };
        });
        setItems(mapped);
      } else {
        setItems([]);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLive();
    intervalRef.current = setInterval(fetchLive, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No live matches — show a rotating message ticker
  if (!loading && items.length === 0) {
    return <NoLiveGamesTicker label={t.liveScores.title} />;
  }

  return (
    <div
      className="relative overflow-hidden rounded-xl py-3"
      style={{ background: 'hsl(222 40% 9%)', border: '1px solid hsl(222 25% 15%)' }}>
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center px-4 gap-2"
      style={{ background: 'linear-gradient(to right, hsl(222 40% 9%) 80%, transparent)' }}>
        <span className="w-2 h-2 rounded-full bg-red-500 live-pulse flex-shrink-0" />
        <span className="text-xs font-bold text-red-400 tracking-widest uppercase whitespace-nowrap">{t.liveScores.title}</span>
      </div>
      <div className="overflow-hidden ml-36">
        <div
          className="flex gap-6 items-center"
          style={{ animation: 'tickerScroll 30s linear infinite', whiteSpace: 'nowrap' }}>
          {[...items, ...items].map((item, i) =>
          <Link
            key={`${item.id}-${i}`}
            href="/live-scores"
            className="flex items-center gap-3 px-4 py-1 rounded-lg flex-shrink-0 transition-colors hover:bg-white/5">
              <span className="text-sm text-slate-300 font-medium">{item.home}</span>
              <span
              className="font-mono font-bold text-base px-2 py-0.5 rounded"
              style={{
                background: item.live ? 'hsl(0 84% 60% / 0.12)' : 'hsl(222 25% 18%)',
                color: item.live ? 'hsl(0 84% 70%)' : 'hsl(222 15% 65%)'
              }}>
                {item.homeScore} – {item.awayScore}
              </span>
              <span className="text-sm text-slate-300 font-medium">{item.away}</span>
              <span
              className="text-xs font-semibold px-1.5 py-0.5 rounded-full ml-1"
              style={{
                background: item.live ? 'hsl(0 84% 60% / 0.15)' : 'hsl(222 25% 18%)',
                color: item.live ? 'hsl(0 84% 70%)' : 'hsl(222 15% 55%)'
              }}>
                {item.minute}
              </span>
              <span className="text-slate-700 ml-2">|</span>
            </Link>
          )}
        </div>
      </div>
    </div>);

}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ card }: {card: StatCard;}) {
  const { t } = useLanguage();
  const Icon = card.icon;
  return (
    <Link
      href={card.href}
      className="group relative rounded-2xl p-5 flex flex-col gap-3 overflow-hidden card-hover"
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at top left, ${card.accent}18 0%, transparent 70%)` }} />
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${card.accent}18` }}>
        <Icon size={20} style={{ color: card.accent }} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'hsl(222 15% 55%)' }}>
          {card.label}
        </p>
        <p className="text-3xl font-bold text-white mt-0.5 font-mono">{card.value}</p>
        <p className="text-xs mt-1" style={{ color: 'hsl(222 15% 55%)' }}>{card.sub}</p>
      </div>
      <div className="flex items-center gap-1 text-xs font-semibold mt-auto" style={{ color: card.accent }}>
        {t.landing.readMore} <ChevronRight size={14} />
      </div>
    </Link>);

}

// ─── News Card ────────────────────────────────────────────────────────────────
function NewsCard({ articleKey, featured = false, wide = false }: {articleKey: typeof NEWS_ARTICLES_KEYS[number];featured?: boolean;wide?: boolean;}) {
  const { t } = useLanguage();
  const tl = t.landing as Record<string, string>;
  const article = {
    ...articleKey,
    title: tl[articleKey.titleKey] ?? articleKey.titleKey,
    excerpt: tl[articleKey.excerptKey] ?? articleKey.excerptKey,
    category: tl[articleKey.categoryKey] ?? articleKey.categoryKey,
    time: tl[articleKey.timeKey] ?? articleKey.timeKey
  };

  if (wide) {
    return (
      <Link
        href={article.href}
        className="group rounded-2xl overflow-hidden card-hover flex flex-col sm:flex-row"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 18%)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
        <div className="relative overflow-hidden flex-shrink-0 h-56 sm:h-auto sm:w-2/5 lg:w-1/3">
          <img
            src={article.image}
            alt={article.alt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent 60%, hsl(222 35% 11% / 0.6) 100%)' }} />
          <span
            className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider"
            style={{ background: 'hsl(25 95% 53% / 0.95)', color: 'white', backdropFilter: 'blur(4px)' }}>
            {article.category}
          </span>
        </div>
        <div className="flex flex-col gap-3 flex-1 p-5 justify-center">
          <h3 className="font-bold text-white leading-snug group-hover:text-orange-400 transition-colors text-lg">
            {article.title}
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: 'hsl(222 15% 65%)' }}>
            {tl[articleKey.excerptKey] ?? ''}
          </p>
          <div className="flex items-center justify-between mt-auto pt-3 border-t" style={{ borderColor: 'hsl(222 25% 18%)' }}>
            <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'hsl(222 15% 50%)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
              {article.time}
            </span>
            <span className="text-xs font-semibold text-orange-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {t.landing.readMore} <ArrowRight size={12} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={article.href}
      className={`group rounded-2xl overflow-hidden card-hover flex flex-col ${featured ? 'row-span-2' : ''}`}
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 18%)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
      <div className={`relative overflow-hidden flex-shrink-0 ${featured ? 'h-72' : 'h-52'}`}>
        <img
          src={article.image}
          alt={article.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, hsl(222 35% 11%) 15%, hsl(222 35% 11% / 0.3) 60%, transparent 100%)' }} />
        <span
          className="absolute top-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider"
          style={{ background: 'hsl(25 95% 53% / 0.95)', color: 'white', backdropFilter: 'blur(4px)' }}>
          {article.category}
        </span>
        {featured &&
        <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-extrabold text-white leading-tight text-xl group-hover:text-orange-300 transition-colors drop-shadow-lg">
              {article.title}
            </h3>
          </div>
        }
      </div>
      <div className={`flex flex-col gap-2 flex-1 ${featured ? 'p-5' : 'p-4'}`}>
        {!featured &&
        <h3 className="font-bold text-white leading-snug group-hover:text-orange-400 transition-colors text-sm">
            {article.title}
          </h3>
        }
        {featured &&
        <p className="text-sm leading-relaxed" style={{ color: 'hsl(222 15% 65%)' }}>
            {article.excerpt}
          </p>
        }
        <div className="flex items-center justify-between mt-auto pt-2 border-t" style={{ borderColor: 'hsl(222 25% 18%)' }}>
          <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'hsl(222 15% 50%)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
            {article.time}
          </span>
          <span className="text-xs font-semibold text-orange-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {t.landing.readMore} <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>);

}

function LiveNewsCard({ article, featured = false }: {article: LiveNewsArticle;featured?: boolean;}) {
  const { t } = useLanguage();
  const shortDescription = article.description || article.excerpt || '';
  const [expanded, setExpanded] = useState(false);
  const fullContent = article.content || '';

  // Featured = wide horizontal card (image left, text right)
  if (featured) {
    return (
      <div
        className="group rounded-2xl overflow-hidden flex flex-col sm:flex-row"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
        {/* Image */}
        <div className="relative overflow-hidden flex-shrink-0 h-56 sm:h-auto sm:w-2/5 lg:w-5/12">
          {article.image ? (
            <img
              src={article.image}
              alt={article.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-full h-full min-h-[220px]" style={{ background: 'hsl(222 35% 14%)' }} />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent 60%, hsl(222 35% 11% / 0.5) 100%)' }} />
          <span
            className="absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
            style={{ background: 'hsl(25 95% 53% / 0.9)', color: 'white' }}>
            {article.category}
          </span>
        </div>
        {/* Text */}
        <div className="flex flex-col gap-3 flex-1 p-5 sm:p-6 justify-center">
          <h3 className="font-extrabold text-white leading-snug text-lg sm:text-xl group-hover:text-orange-300 transition-colors">
            {article.title}
          </h3>
          {shortDescription && (
            <p className="text-sm leading-relaxed line-clamp-3" style={{ color: 'hsl(222 15% 65%)' }}>
              {shortDescription}
            </p>
          )}
          {expanded && fullContent && (
            <div
              className="text-sm leading-relaxed mt-1 pt-3 border-t"
              style={{ color: 'hsl(222 15% 70%)', borderColor: 'hsl(222 25% 18%)' }}>
              {fullContent}
            </div>
          )}
          <div className="flex items-center justify-between mt-auto pt-3 border-t" style={{ borderColor: 'hsl(222 25% 18%)' }}>
            <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'hsl(222 15% 50%)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
              {article.source_name ? `${article.source_name} · ` : ''}{article.time}
            </span>
            {fullContent ? (
              <button
                onClick={() => setExpanded((prev) => !prev)}
                className="text-xs font-semibold text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1 flex-shrink-0 bg-transparent border-none cursor-pointer p-0">
                {expanded ? t.landing.showLess : t.landing.readMore} <ArrowRight size={12} className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              </button>
            ) : (
              <span className="text-xs font-semibold text-orange-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {t.landing.readMore} <ArrowRight size={12} />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard vertical card
  return (
    <div
      className="group rounded-2xl overflow-hidden flex flex-col"
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
      <div className="relative overflow-hidden flex-shrink-0 h-44">
        {article.image ?
        <img
          src={article.image}
          alt={article.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {(e.currentTarget as HTMLImageElement).style.display = 'none';}} /> :
        <div className="w-full h-full" style={{ background: 'hsl(222 35% 14%)' }} />
        }
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, hsl(222 35% 11%) 0%, transparent 60%)' }} />
        <span
          className="absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
          style={{ background: 'hsl(25 95% 53% / 0.9)', color: 'white' }}>
          {article.category}
        </span>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-bold text-white leading-snug text-sm group-hover:text-orange-400 transition-colors">
          {article.title}
        </h3>
        {shortDescription && (
          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'hsl(222 15% 60%)' }}>
            {shortDescription}
          </p>
        )}
        {expanded && fullContent && (
          <div
            className="text-sm leading-relaxed mt-1 pt-3 border-t"
            style={{ color: 'hsl(222 15% 70%)', borderColor: 'hsl(222 25% 18%)' }}>
            {fullContent}
          </div>
        )}
        <div className="flex items-center justify-between mt-auto pt-2">
          <span className="text-xs" style={{ color: 'hsl(222 15% 50%)' }}>
            {article.source_name ? `${article.source_name} · ` : ''}{article.time}
          </span>
          {fullContent ? (
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="text-xs font-semibold text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1 flex-shrink-0 bg-transparent border-none cursor-pointer p-0">
              {expanded ? t.landing.showLess : t.landing.readMore} <ArrowRight size={12} className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
            </button>
          ) : null}
        </div>
      </div>
    </div>);

}

// ─── Last Game Result ─────────────────────────────────────────────────────────
interface RecentGameResult {
  homeName: string;
  homeFlag: string;
  homeLogo: string | null;
  homeScore: number;
  awayName: string;
  awayFlag: string;
  awayLogo: string | null;
  awayScore: number;
  statusLabel: string;
  date: string;
}

function LastGameResult() {
  const [game, setGame] = useState<RecentGameResult | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch('/api/data')
      .then((r) => r.json())
      .then((data) => {
        const f = data?.recentGame;
        if (!f) { setLoaded(true); return; }
        const statusShort: string = f?.fixture?.status?.short ?? '';
        const statusMap: Record<string, string> = {
          FT: 'Full Time', AET: 'After Extra Time', PEN: 'Penalties',
        };
        const label = statusMap[statusShort] ?? 'Finished';
        const dateStr: string = f?.fixture?.date ?? '';
        const dateLabel = dateStr
          ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : '';
        setGame({
          homeName: f.teams.home.name,
          homeFlag: getFlag(f.teams.home.name),
          homeLogo: f.teams.home.logo ?? null,
          homeScore: f.goals.home ?? 0,
          awayName: f.teams.away.name,
          awayFlag: getFlag(f.teams.away.name),
          awayLogo: f.teams.away.logo ?? null,
          awayScore: f.goals.away ?? 0,
          statusLabel: label,
          date: dateLabel,
        });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded || !game) return null;

  const homeWon = game.homeScore > game.awayScore;
  const awayWon = game.awayScore > game.homeScore;

  return null;
}

// ─── Newsletter ───────────────────────────────────────────────────────────────
function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{message: string;type: 'success' | 'error';} | null>(null);
  const { t } = useLanguage();

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    // Basic client-side email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed })
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Thanks, check your inbox!', 'success');
        setEmail('');
      } else {
        showToast(data?.error || 'Something went wrong. Please try again.', 'error');
      }
    } catch {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="rounded-2xl p-8 md:p-12 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, hsl(222 40% 11%) 0%, hsl(222 35% 14%) 50%, hsl(25 60% 12%) 100%)',
        border: '1px solid hsl(222 25% 18%)'
      }}>

      {/* Toast notification */}
      {toast &&
      <div
        className="absolute top-4 right-4 z-20 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold shadow-lg transition-all"
        style={{
          background: toast.type === 'success' ? 'hsl(142 71% 45% / 0.15)' : 'hsl(0 84% 60% / 0.15)',
          border: `1px solid ${toast.type === 'success' ? 'hsl(142 71% 45% / 0.4)' : 'hsl(0 84% 60% / 0.4)'}`,
          color: toast.type === 'success' ? 'hsl(142 71% 65%)' : 'hsl(0 84% 70%)'
        }}>
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          <span>{toast.message}</span>
        </div>
      }

      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(circle, hsl(25 95% 53% / 0.12) 0%, transparent 70%)' }} />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(circle, hsl(189 94% 51% / 0.08) 0%, transparent 70%)' }} />

      <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start mb-3">
            <Bell size={18} className="text-orange-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-orange-400">{t.landing.newsletter}</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-2">
            {t.landing.newsletter}<br />
            <span style={{ color: 'hsl(189 94% 65%)' }}>{t.landing.heroSubtitle}</span>
          </h2>
          <p className="text-sm" style={{ color: 'hsl(222 15% 60%)' }}>
            {t.landing.newsletterDesc}
          </p>
        </div>

        <div className="w-full md:w-auto md:min-w-[340px]">
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.landing.emailPlaceholder}
              required
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-xl text-sm text-white placeholder-slate-500 outline-none focus:ring-2 transition-all disabled:opacity-60"
              style={{
                background: 'hsl(222 40% 9%)',
                border: '1px solid hsl(222 25% 20%)'
              }} />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary whitespace-nowrap flex items-center gap-2 justify-center disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ?
              <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {t.landing.subscribing}
                </span> :

              <>{t.landing.subscribe} <ArrowRight size={16} /></>
              }
            </button>
          </form>

          <div className="flex items-center gap-4 mt-5 justify-center md:justify-start">
            <span className="text-xs" style={{ color: 'hsl(222 15% 50%)' }}>{t.landing.followUs}:</span>
            {[
            { icon: Share2, label: 'Twitter', color: 'hsl(189 94% 51%)' },
            { icon: Play, label: 'YouTube', color: 'hsl(0 84% 60%)' },
            { icon: Camera, label: 'Instagram', color: 'hsl(25 95% 53%)' }].
            map(({ icon: SocialIcon, label, color }) => {
              const SocialIconComponent = SocialIcon as React.ElementType;
              return (
                <button
                  key={label}
                  aria-label={label}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110"
                  style={{ background: `${color}18`, color }}>
                  <SocialIconComponent size={16} />
                </button>);
            })}
          </div>
        </div>
      </div>
    </div>);
}

// ─── KPI Stats Hook ───────────────────────────────────────────────────────────
interface KpiStats {
  latestUpdates: string;
  upcomingMatches: string;
  standingsGroups: string;
  liveNow: string;
  liveMatchesLabel: string;
  hasLiveMatches: boolean;
  upcomingCount: number;
}

function useKpiStats(): KpiStats {
  const [stats, setStats] = useState<KpiStats>({
    latestUpdates: '—',
    upcomingMatches: '—',
    standingsGroups: '—',
    liveNow: '—',
    liveMatchesLabel: '—',
    hasLiveMatches: false,
    upcomingCount: 0
  });

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/data');
      if (!res.ok) throw new Error(`/api/data returned ${res.status}`);
      const data = await res.json();

      const liveFixtures: LiveFixture[] = Array.isArray(data.liveScores) ? data.liveScores : [];
      const upcomingFixtures: LiveFixture[] = Array.isArray(data.latestUpdates) ? data.latestUpdates : [];

      const liveCount = liveFixtures.length;
      const upcomingCount = upcomingFixtures.length;

      // "Latest updates" = today's fixtures count, fallback to upcoming
      const now = new Date();
      const todayStr = now.toISOString().slice(0, 10);
      const todayFixtures = upcomingFixtures.filter((f) => f.fixture.date.startsWith(todayStr));
      const latestCount = todayFixtures.length > 0 ? todayFixtures.length : Math.min(upcomingCount, 10);

      setStats({
        latestUpdates: latestCount > 0 ? String(latestCount) : '—',
        upcomingMatches: upcomingCount > 0 ? String(upcomingCount) : '—',
        standingsGroups: '8',
        liveNow: liveCount > 0 ? String(liveCount) : '0',
        liveMatchesLabel: liveCount > 0 ? String(liveCount) : '0',
        hasLiveMatches: liveCount > 0,
        upcomingCount
      });
    } catch {
      // keep previous values on error
    }
  };
  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return stats;
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  const kpi = useKpiStats();
  const [liveNews, setLiveNews] = useState<LiveNewsArticle[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadNews() {
      setNewsLoading(true);
      try {
        // Fetch from daily_news Supabase table via the dedicated API endpoint
        const res = await fetch(`/api/daily-news?lang=${language}`, { cache: 'no-store' });
        const data = await res.json();
        if (!cancelled && Array.isArray(data.articles) && data.articles.length > 0) {
          setLiveNews(data.articles);
        }
      } catch {
        // keep empty state on error
      } finally {
        if (!cancelled) setNewsLoading(false);
      }
    }

    loadNews();

    // Auto-refresh every 24 hours
    const refreshInterval = setInterval(loadNews, 24 * 60 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const STAT_CARDS: StatCard[] = [
  {
    id: 'sc1',
    icon: TrendingUp,
    label: t.landing.latestUpdates,
    value: kpi.latestUpdates,
    sub: kpi.latestUpdates === '—' ? t.landing.noMatchesToday : `${kpi.latestUpdates === '1' ? (language === 'es' ? 'partido hoy' : 'match today') : (language === 'es' ? t.landing.matchesToday : t.landing.matchesToday)}`,
    accent: 'hsl(25 95% 53%)',
    href: '/live-scores'
  },
  {
    id: 'sc2',
    icon: Calendar,
    label: t.landing.upcomingMatches,
    value: kpi.upcomingMatches,
    sub: kpi.upcomingMatches === '—' ? t.landing.scheduleLoading : t.landing.scheduledFixtures,
    accent: 'hsl(189 94% 51%)',
    href: '/live-games-dashboard'
  },
  {
    id: 'sc3',
    icon: Trophy,
    label: t.landing.standingsLabel,
    value: kpi.standingsGroups,
    sub: t.standings.subtitle,
    accent: 'hsl(45 93% 47%)',
    href: '/standings'
  },
  {
    id: 'sc4',
    icon: Activity,
    label: t.landing.liveNow,
    value: kpi.hasLiveMatches ? kpi.liveNow : '—',
    sub: kpi.hasLiveMatches ? `${kpi.liveMatchesLabel} ${t.landing.matchesLive}` : t.landing.noMatchesInProgress,
    accent: kpi.hasLiveMatches ? 'hsl(0 84% 60%)' : 'hsl(222 15% 45%)',
    href: '/live-scores'
  }];


  return (
    <div className="min-h-screen" style={{ background: 'hsl(222 47% 6%)' }}>
      {/* ── Top Nav ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrollY > 60 || mobileMenuOpen ? 'hsl(222 47% 6% / 0.97)' : 'transparent',
          backdropFilter: scrollY > 60 || mobileMenuOpen ? 'blur(12px)' : 'none',
          borderBottom: scrollY > 60 || mobileMenuOpen ? '1px solid hsl(222 25% 15%)' : 'none'
        }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/landing" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/assets/images/0F73B197-0645-40FD-847D-C4CC2CCBEBDF-1781714303568.png" alt="NOTICOPA logo" width={28} height={28} className="object-contain flex-shrink-0" />
            <span className="font-bold text-white text-lg tracking-tight">NOTICOPA</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {[
            { label: t.nav.liveScores, href: '/live-scores' },
            { label: t.nav.dashboard, href: '/live-games-dashboard' },
            { label: t.nav.standings, href: '/standings' },
            { label: t.nav.pastGames, href: '/past-games' },
            { label: t.nav.favorites, href: '/favorites' }].
            map((link) =>
            <Link
              key={link.href}
              href={link.href}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-all">
{link.label}
</Link>
            )}
          </nav>
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <button
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 hover:scale-105"
              style={{
                background: 'hsl(222 35% 14%)',
                border: '1px solid hsl(222 25% 22%)',
                color: 'hsl(25 95% 65%)'
              }}
              aria-label={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
              title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}>
              
              <span className="text-sm leading-none">{language === 'en' ? '🇺🇸' : '🇪🇸'}</span>
              <span className="tracking-wide">{language === 'en' ? 'EN' : 'ES'}</span>
            </button>

            {/* Auth Button — desktop */}
            <Link
              href="/live-games-dashboard"
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
              style={{ background: 'hsl(142 71% 45% / 0.15)', border: '1px solid hsl(142 71% 45% / 0.35)', color: 'hsl(142 71% 60%)' }}>
              <CheckCircle size={14} />
              <span>{t.landing.accessDashboard}</span>
            </Link>

            <Link href="/live-games-dashboard" className="btn-primary text-sm hidden md:flex items-center gap-2">
              <Zap size={15} />
              {t.landing.watchLive}
            </Link>
            {/* Hamburger button — mobile only */}
            <button
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 active:scale-95"
              style={{ background: mobileMenuOpen ? 'hsl(25 95% 53% / 0.15)' : 'hsl(222 35% 14%)', border: `1px solid ${mobileMenuOpen ? 'hsl(25 95% 53% / 0.4)' : 'hsl(222 25% 22%)'}` }}
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}>
              
              {mobileMenuOpen ? <X size={20} className="text-orange-400" /> : <Menu size={20} className="text-slate-200" />}
            </button>
          </div>
        </div>

        {/* ── Mobile Dropdown Menu ── */}
        {mobileMenuOpen &&
        <>
            {/* Backdrop overlay */}
            <div
            className="md:hidden fixed inset-0 top-16 z-40"
            style={{ background: 'hsl(222 47% 4% / 0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => setMobileMenuOpen(false)} />
          
            <div
            className="md:hidden relative z-50 mx-3 mb-3 rounded-2xl overflow-hidden"
            style={{
              background: 'hsl(222 40% 8%)',
              border: '1px solid hsl(222 25% 18%)',
              boxShadow: '0 20px 60px hsl(222 47% 3% / 0.8), 0 0 0 1px hsl(222 25% 20% / 0.3)'
            }}>
            
              {/* Menu header */}
              <div className="px-5 pt-5 pb-3">
                <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'hsl(222 25% 45%)' }}>{t.dashboard.navigation}</p>
              </div>

              {/* Menu items */}
              <div className="px-3 pb-3 flex flex-col gap-1">
                {[
              { label: t.nav.liveScores, href: '/live-scores', icon: '⚡', desc: t.liveScores.subtitle },
              { label: t.nav.dashboard, href: '/live-games-dashboard', icon: '📅', desc: t.dashboard.upcoming },
              { label: t.nav.standings, href: '/standings', icon: '🏆', desc: t.standings.subtitle },
              { label: t.nav.pastGames, href: '/past-games', icon: '🕐', desc: t.pastGames.subtitle },
              { label: t.nav.favorites, href: '/favorites', icon: '⭐', desc: t.favorites.subtitle }].
              map((link) =>
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-4 px-4 py-4 rounded-xl transition-all duration-150 active:scale-[0.98] group"
                style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                
                    <span className="text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'hsl(222 35% 15%)' }}>{link.icon}</span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-white leading-tight">{link.label}</span>
                      <span className="text-xs mt-0.5" style={{ color: 'hsl(222 25% 50%)' }}>{link.desc}</span>
                    </div>
                    <svg className="ml-auto flex-shrink-0 opacity-30 group-hover:opacity-60 transition-opacity" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </Link>
              )}
              </div>

              {/* Divider */}
              <div className="mx-5 mb-3" style={{ height: '1px', background: 'hsl(222 25% 16%)' }} />

              {/* Auth row — mobile */}
              <div className="px-3 pb-3">
                <Link
                  href="/live-games-dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-[0.98]"
                  style={{ background: 'hsl(142 71% 45% / 0.12)', border: '1px solid hsl(142 71% 45% / 0.3)', color: 'hsl(142 71% 60%)' }}>
                  <CheckCircle size={16} />
                  {t.landing.accessDashboard}
                </Link>
              </div>

              {/* Watch Live CTA */}
              <div className="px-3 pb-4">
                <Link
                href="/live-games-dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2.5 px-4 py-4 rounded-xl text-sm font-bold text-white transition-all duration-150 active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, hsl(25 95% 53%), hsl(25 95% 43%))', boxShadow: '0 4px 20px hsl(25 95% 53% / 0.35)' }}>
                
                  <Zap size={16} />
                  {t.landing.watchLive}
                </Link>
              </div>
            </div>
          </>
        }
      </header>

      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/assets/images/stadium_hero_bg.png')`,
            transform: `translateY(${scrollY * 0.3}px)`,
            willChange: 'transform'
          }} />
        {/* Strong black overlay */}
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.55)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, hsl(222 47% 6% / 0.65) 0%, hsl(222 47% 6% / 0.45) 40%, hsl(222 47% 6% / 0.85) 80%, hsl(222 47% 6%) 100%)' }} />
        {/* Strong vignette */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 20%, hsl(222 47% 3% / 0.75) 70%, hsl(222 47% 3% / 0.92) 100%)' }} />
        <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")', backgroundSize: '200px' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-24 pb-32">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
          style={{ background: 'hsl(25 95% 53% / 0.15)', border: '1px solid hsl(25 95% 53% / 0.3)' }}>
            <span className="w-2 h-2 rounded-full bg-red-500 live-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest text-orange-400">{t.landing.liveCoverage}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6" style={{ color: '#ffffff', textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}>
            {t.landing.heroTitle}{' '}
            <span
              className="block"
              style={{
                color: '#ff6b00',
                textShadow: '0 0 40px rgba(255,107,0,0.5), 0 2px 20px rgba(0,0,0,0.5)'
              }}>
              {t.landing.heroTitleAccent}
            </span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl font-medium mb-10 max-w-2xl mx-auto" style={{ color: 'hsl(222 15% 75%)' }}>
            {t.landing.heroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              href="/live-games-dashboard"
              className="flex items-center gap-2.5 text-base font-bold px-10 py-4 rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #ff6b00, #ff8c00)', color: '#ffffff', boxShadow: '0 4px 24px rgba(255,107,0,0.55), 0 1px 0 rgba(255,255,255,0.15) inset' }}>
              <Zap size={20} />
              {t.landing.watchLive}
            </Link>
            <Link
              href="/live-games-dashboard"
              className="flex items-center gap-2.5 text-base font-bold px-10 py-4 rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.35)', color: '#ffffff', backdropFilter: 'blur(8px)', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
              {t.nav.dashboard} <ArrowRight size={20} />
            </Link>
          </div>

          <LastGameResult />

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
            <span className="text-xs text-slate-400 uppercase tracking-widest">{t.landing.scroll}</span>
            <div className="w-px h-8 bg-gradient-to-b from-slate-400 to-transparent" />
          </div>
        </div>
      </section>

      {/* ── Live Ticker ── */}
      <div className="sticky top-16 z-40 px-4 sm:px-6 lg:px-8 -mt-1 pb-1"
      style={{ background: 'hsl(222 47% 6%)' }}>
        <div className="max-w-7xl mx-auto">
          <LiveTicker />
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* ── Quick Stats ── */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 rounded-full bg-orange-500" />
            <h2 className="text-xl font-bold text-white">{t.landing.latestUpdates}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {STAT_CARDS.map((card) => <StatCard key={card.id} card={card} />)}
          </div>
        </section>

        {/* ── Top Stories ── */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-orange-500" />
              <h2 className="text-xl font-bold text-white">{t.landing.topStories}</h2>
            </div>
            <Link href="/news" className="text-sm font-semibold text-orange-400 flex items-center gap-1 hover:text-orange-300 transition-colors">
              {t.news.viewAll} <ChevronRight size={16} />
            </Link>
          </div>

          {newsLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <svg className="animate-spin w-8 h-8 text-orange-400" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-sm font-medium" style={{ color: 'hsl(222 15% 55%)' }}>{t.news.loading}</span>
            </div>
          ) : liveNews.length > 0 ? (
            <div className="flex flex-col gap-5">
              {/* Featured article — wide horizontal card */}
              {liveNews[0] && (
                <LiveNewsCard article={liveNews[0]} featured />
              )}
              {/* Secondary articles — 3-column row */}
              {liveNews.slice(1, 4).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {liveNews.slice(1, 4).map((article) => (
                    <LiveNewsCard key={article.id} article={article} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-500 text-sm">{t.news.noStories}</div>
          )}
        </section>

        {/* ── Quick Links Banner ── */}
        <section>
          <div
            className="rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-4"
            style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            {[
            { icon: Zap, label: t.nav.liveScores, sub: t.liveScores.subtitle, href: '/live-scores', color: 'hsl(0 84% 60%)' },
            { icon: Trophy, label: t.nav.standings, sub: t.standings.subtitle, href: '/standings', color: 'hsl(45 93% 47%)' },
            { icon: Activity, label: t.nav.matchDetail, sub: t.team.overview, href: '/game-detail', color: 'hsl(189 94% 51%)' },
            { icon: Star, label: t.nav.favorites, sub: t.favorites.subtitle, href: '/favorites', color: 'hsl(25 95% 53%)' }].
            map(({ icon: LinkIcon, label, sub, href, color }) => {
              const LinkIconComponent = LinkIcon as React.ElementType;
              return (
                <Link
                  key={href}
                  href={href}
                  className="group flex items-center gap-4 p-4 rounded-xl transition-all hover:bg-white/5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{ background: `${color}18` }}>
                    <LinkIconComponent size={20} style={{ color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white">{label}</p>
                    <p className="text-xs truncate" style={{ color: 'hsl(222 15% 55%)' }}>{sub}</p>
                  </div>
                  <ChevronRight size={16} className="ml-auto text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
                </Link>);

            })}
          </div>
        </section>

        {/* ── Newsletter & Social ── */}
        <section>
          <NewsletterSection />
        </section>
      </div>

      {/* ── Footer ── */}
      <footer className="border-t mt-8" style={{ borderColor: 'hsl(222 25% 14%)', background: 'hsl(222 40% 7%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/assets/images/0F73B197-0645-40FD-847D-C4CC2CCBEBDF-1781714303568.png" alt="NOTICOPA logo" width={22} height={22} className="object-contain flex-shrink-0" />
            <span className="font-bold text-white text-sm">NOTICOPA</span>
            <span className="text-xs ml-2" style={{ color: 'hsl(222 15% 45%)' }}>{t.landing.copyright}</span>
          </div>
          <div className="flex items-center gap-6">
            {[
            { label: t.nav.dashboard, href: '/live-games-dashboard' },
            { label: t.common.live, href: '/live-scores' },
            { label: t.nav.standings, href: '/standings' },
            { label: t.nav.bracket, href: '/bracket' }].
            map((link) =>
            <Link key={link.href} href={link.href} className="text-xs transition-colors hover:text-white" style={{ color: 'hsl(222 15% 50%)' }}>
                {link.label}
              </Link>
            )}
          </div>
        </div>
      </footer>

      <style jsx global>{`
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>);

}